import React, { useState } from 'react';
import './App.css';

type Player = 'X' | 'O' | null;

interface GameState {
  board: Player[];
  currentPlayer: 'X' | 'O';
  winner: Player;
  isDraw: boolean;
  gameOver: boolean;
}

function App() {
  const [playerNames, setPlayerNames] = useState<{ X: string; O: string }>({ X: '', O: '' });
  const [nameInputs, setNameInputs] = useState<{ X: string; O: string }>({ X: '', O: '' });
  const [namesSubmitted, setNamesSubmitted] = useState(false);
  const [gameState, setGameState] = useState<GameState>({
    board: Array(9).fill(null),
    currentPlayer: 'X',
    winner: null,
    isDraw: false,
    gameOver: false,
  });

  const calculateWinner = (squares: Player[]): Player => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ];

    for (const [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  const getWinningLine = (squares: Player[]): number[] | null => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6] // diagonals
    ];

    for (const [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return [a, b, c];
      }
    }
    return null;
  };

  const checkDraw = (squares: Player[]): boolean => {
    return squares.every(square => square !== null);
  };

  const handleClick = (index: number) => {
    if (gameState.board[index] || gameState.gameOver) {
      return;
    }

    const newBoard = [...gameState.board];
    newBoard[index] = gameState.currentPlayer;

    const winner = calculateWinner(newBoard);
    const isDraw = checkDraw(newBoard);
    const gameOver = winner !== null || isDraw;

    setGameState({
      board: newBoard,
      currentPlayer: gameState.currentPlayer === 'X' ? 'O' : 'X',
      winner,
      isDraw,
      gameOver,
    });
  };

  const resetGame = () => {
    setGameState({
      board: Array(9).fill(null),
      currentPlayer: 'X',
      winner: null,
      isDraw: false,
      gameOver: false,
    });
  };

  const handleNameChange = (player: 'X' | 'O', value: string) => {
    setNameInputs((prev) => ({ ...prev, [player]: value }));
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameInputs.X.trim() && nameInputs.O.trim()) {
      setPlayerNames({ X: nameInputs.X.trim(), O: nameInputs.O.trim() });
      setNamesSubmitted(true);
    }
  };

  const renderSquare = (index: number) => {
    const value = gameState.board[index];
    const winningLine = getWinningLine(gameState.board);
    const isWinningSquare = winningLine && winningLine.includes(index);
    // Only display 'X' or 'O' in the squares
    return (
      <button
        className={`square ${value ? 'filled' : ''} ${isWinningSquare ? 'winning' : ''}`}
        onClick={() => handleClick(index)}
        disabled={gameState.gameOver}
      >
        {value}
      </button>
    );
  };

  const getStatusMessage = () => {
    if (gameState.winner) {
      return `Winner: ${playerNames[gameState.winner] || gameState.winner}!`;
    } else if (gameState.isDraw) {
      return "It's a draw!";
    } else {
      return `Next player: ${playerNames[gameState.currentPlayer] || gameState.currentPlayer}`;
    }
  };

  if (!namesSubmitted) {
    return (
      <div className="App">
        <div className="game-container">
          <h1 className="game-title">Tic Tac Toe</h1>
          <form className="name-form" onSubmit={handleNameSubmit}>
            <div className="name-input-group">
              <label htmlFor="player-x">Player 1 (X):</label>
              <input
                id="player-x"
                type="text"
                value={nameInputs.X}
                onChange={e => handleNameChange('X', e.target.value)}
                placeholder="Enter Player 1 name"
                required
                autoFocus
              />
            </div>
            <div className="name-input-group">
              <label htmlFor="player-o">Player 2 (O):</label>
              <input
                id="player-o"
                type="text"
                value={nameInputs.O}
                onChange={e => handleNameChange('O', e.target.value)}
                placeholder="Enter Player 2 name"
                required
              />
            </div>
            <button className="start-button" type="submit">Start Game</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="game-container">
        <h1 className="game-title">Tic Tac Toe</h1>
        <div className="status">
          <h2 className="status-message">{getStatusMessage()}</h2>
        </div>
        <div className="board">
          <div className="board-row">
            {renderSquare(0)}
            {renderSquare(1)}
            {renderSquare(2)}
          </div>
          <div className="board-row">
            {renderSquare(3)}
            {renderSquare(4)}
            {renderSquare(5)}
          </div>
          <div className="board-row">
            {renderSquare(6)}
            {renderSquare(7)}
            {renderSquare(8)}
          </div>
        </div>
        <button className="reset-button" onClick={resetGame}>
          New Game
        </button>
      </div>
    </div>
  );
}

export default App;
